import { useCallback, useId, useState, type KeyboardEvent } from 'react';

/** The open / closed state of one field's guidance. See FieldGuidance.tsx for what the guidance is and how it behaves. */
export interface GuidanceState {
  open: boolean;
  toggle: () => void;
  close: (returnFocus?: boolean) => void;
  panelId: string;
  /** Escape handler for the wrapper around the button, the panel and the control. */
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
}

/** `setButton` is a callback ref for the information button (so focus can return to it); it is kept apart from the state the render reads. */
export function useFieldGuidance(): { state: GuidanceState; setButton: (el: HTMLButtonElement | null) => void } {
  const [open, setOpen] = useState(false);
  const [button, setButton] = useState<HTMLButtonElement | null>(null);
  const panelId = `${useId()}-guidance`;
  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) button?.focus();
  }, [button]);
  const toggle = useCallback(() => setOpen((v) => !v), []);
  const onKeyDown = useCallback((e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Escape' || !open) return;
    // Closes only the guidance: a parent dialog must stay open.
    e.stopPropagation();
    e.preventDefault();
    close(true);
  }, [open, close]);
  return { state: { open, toggle, close, panelId, onKeyDown }, setButton };
}
